import { Button, SimpleGrid, Text } from '@mantine/core';
import { FC } from 'react';
import { Popup } from 'react-map-gl';

import type { GpAssignmentMapPoint } from '../../data-access/types/gp-sampling-assignment';

import { organicLabelKey, samplingStatusLabelKey } from './assignment-map-constants';

import { TsrButtonLink } from '@ui';

export interface AssignmentPointPopupData {
  longitude: number;
  latitude: number;
  point: GpAssignmentMapPoint;
}

interface AssignmentPointPopupProps {
  canForceOrganic?: boolean;
  data: AssignmentPointPopupData | null;
  forceOrganicLoading?: boolean;
  i18n: (key: string, options?: Record<string, unknown>) => string;
  onClose: () => void;
  onSetOrganicForced?: (pointId: number, forced: boolean) => void;
  showContractLink: boolean;
}

function formatIsoDate(value: string): string {
  const [year, month, day] = value.slice(0, 10).split('-');
  if (!year || !month || !day) {
    return value;
  }
  return `${day}/${month}/${year}`;
}

function PopupRow({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <Text c="dimmed" fw={500} size="xs" tt="uppercase">
        {label}
      </Text>
      <Text lineClamp={2} size="sm">{value}</Text>
    </div>
  );
}

function bulkSamplePopup(
  point: GpAssignmentMapPoint,
  i18n: (key: string, options?: Record<string, unknown>) => string,
): { label: string; value: string } | null {
  if (point.analysis_type === 'granulo') {
    return {
      label: i18n('pointPopupGranulo'),
      value: i18n('pointPopupGranuloStandalone'),
    };
  }
  if (point.analysis_type === 'density') {
    return {
      label: i18n('pointPopupDensity'),
      value: i18n('pointPopupDensityStandalone'),
    };
  }
  if (!point.twin_id) {
    return null;
  }
  if (point.twin_analysis_type === 'density') {
    return {
      label: i18n('pointPopupDensity'),
      value: i18n('pointPopupDensityLinked', { id: point.twin_id }),
    };
  }
  return {
    label: i18n('pointPopupGranulo'),
    value: i18n('pointPopupGranuloLinked', { id: point.twin_id }),
  };
}

export const AssignmentPointPopup: FC<AssignmentPointPopupProps> = ({
  canForceOrganic = false,
  data,
  forceOrganicLoading = false,
  i18n,
  onClose,
  onSetOrganicForced,
  showContractLink,
}) => {
  if (!data) return null;

  const { point, latitude, longitude } = data;
  const bulkSample = bulkSamplePopup(point, i18n);
  const samplerName = point.sampler
    ? `${point.sampler.first_name} ${point.sampler.last_name}`
    : null;

  return (
    <Popup
      anchor="bottom"
      closeButton
      closeOnClick={false}
      closeOnMove={false}
      latitude={latitude}
      longitude={longitude}
      maxWidth="420px"
      offset={[0, -10]}
      onClose={onClose}
    >
      <SimpleGrid cols={2} spacing="sm" verticalSpacing={6}>
        <PopupRow label={i18n('pointPopupId')} value={`#${point.id}`} />
        <PopupRow
          label={i18n('pointPopupSamplingPlan')}
          value={`#${point.sampling_plan_id}`}
        />
        {point.code ? (
          <PopupRow label={i18n('pointPopupCode')} value={point.code} />
        ) : null}
        {point.farm_name ? (
          <PopupRow label={i18n('pointPopupFarm')} value={point.farm_name} />
        ) : null}
        {point.field_name ? (
          <PopupRow label={i18n('pointPopupField')} value={point.field_name} />
        ) : null}
        <PopupRow
          label={i18n('pointPopupCoordinates')}
          value={`${latitude.toFixed(5)}, ${longitude.toFixed(5)}`}
        />
        <PopupRow
          label={i18n('pointPopupOrganic')}
          value={
            canForceOrganic && point.organic_forced_ok
              ? `${i18n(organicLabelKey(point.organic_status))} · ${i18n('organicForcedShort')}`
              : i18n(organicLabelKey(point.organic_status))
          }
        />
        {point.last_organic ? (
          <PopupRow
            label={i18n('pointPopupLastOrganic')}
            value={formatIsoDate(point.last_organic)}
          />
        ) : null}
        <PopupRow
          label={i18n('pointPopupSampling')}
          value={i18n(samplingStatusLabelKey(point.status))}
        />
        {point.sampled_at ? (
          <PopupRow
            label={i18n('pointPopupSampledAt')}
            value={formatIsoDate(point.sampled_at)}
          />
        ) : null}
        {samplerName ? (
          <PopupRow label={i18n('pointPopupSampler')} value={samplerName} />
        ) : null}
        <PopupRow
          label={i18n('pointPopupLaboratory')}
          value={point.laboratory?.name ?? i18n('filterWithoutLaboratory')}
        />
        {point.telepac_code ? (
          <PopupRow label={i18n('pointPopupTelepac')} value={point.telepac_code} />
        ) : null}
        {point.crop_code ? (
          <PopupRow label={i18n('pointPopupCrop')} value={point.crop_code} />
        ) : null}
        {point.sms_active ? (
          <PopupRow label={i18n('pointPopupSms')} value={i18n('pointPopupSmsActive')} />
        ) : null}
        {bulkSample ? (
          <PopupRow label={bulkSample.label} value={bulkSample.value} />
        ) : null}
        {canForceOrganic && onSetOrganicForced && point.organic_status !== 'ok' ? (
          <Button
            color="orange"
            loading={forceOrganicLoading}
            onClick={() => onSetOrganicForced(point.id, true)}
            size="compact-sm"
            style={{ gridColumn: '1 / -1' }}
            variant="light"
          >
            {i18n('forceOrganicOk')}
          </Button>
        ) : null}
        {canForceOrganic && onSetOrganicForced && point.organic_forced_ok ? (
          <Button
            loading={forceOrganicLoading}
            onClick={() => onSetOrganicForced(point.id, false)}
            size="compact-sm"
            style={{ gridColumn: '1 / -1' }}
            variant="subtle"
          >
            {i18n('clearOrganicForce')}
          </Button>
        ) : null}
        {showContractLink ? (
          <TsrButtonLink
            params={{ contractId: String(point.contract_id) }}
            size="compact-sm"
            style={{ gridColumn: '1 / -1' }}
            to="/os/contracts/$contractId"
            variant="light"
          >
            {i18n('pointPopupContract')}
          </TsrButtonLink>
        ) : null}
      </SimpleGrid>
    </Popup>
  );
};
